import { Input, Stack } from '@etg/wings';
import { MaskedInput, getInputMaskConfig } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { constants, date, string } from '@eti/utils';
import type { ComponentProps } from 'react';
import type { WrappedFieldProps } from 'redux-form';

const { DATE_PATTERN } = constants.inputFieldPatterns;

interface MaskedDateInputProps
  extends WrappedFieldProps,
    Omit<ComponentProps<typeof MaskedInput>, 'mask'> {
  'data-testid'?: string;
  dateFormat?: string;
  description?: string;
  isReadOnly?: boolean;
  name?: string;
  shouldMarkInputAsRequired?: boolean;
  [key: string]: any;
}

const MaskedDateInput = ({
  'data-testid': dataTestid,
  dateFormat,
  description,
  input,
  isReadOnly,
  label,
  meta,
  name,
  shouldMarkInputAsRequired,
  ...props
}: MaskedDateInputProps) => {
  const { nonDebugT } = useTranslation();
  const hasError = Boolean(meta.invalid && !meta.active && (input.value !== '' || meta.touched));
  const errorId = hasError ? `${name}-error` : undefined;
  const resolvedLabel = shouldMarkInputAsRequired
    ? nonDebugT('Input.Field.Required', label)
    : label;
  const descriptionId = `${name}-description`;
  const formattedDescription = string.insertArgument(description, dateFormat?.toUpperCase());
  const { mask, pipe } = getInputMaskConfig(date.getBirthDateConstraints(new Date()), dateFormat);

  return (
    <Stack spacing={4}>
      <Input.Label htmlFor={name}>{resolvedLabel}</Input.Label>
      <MaskedInput
        autoCorrect="off"
        data-testid={dataTestid}
        descriptionId={descriptionId}
        errorId={errorId}
        hasError={hasError}
        id={name}
        inputMode="numeric"
        isReadOnly={isReadOnly}
        mask={mask}
        pattern={DATE_PATTERN}
        pipe={pipe}
        placeholder={dateFormat?.toUpperCase()}
        spellCheck="false"
        {...input}
        {...props}
      />
      <Input.Meta
        description={formattedDescription}
        descriptionId={descriptionId}
        error={meta.error}
        errorId={errorId}
        hasError={hasError}
      />
    </Stack>
  );
};

export default MaskedDateInput;
